import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { GfgButton } from "@/components/ui/GfgButton";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { useSubscriptionSetupIntent } from "@/hooks/useSubscriptionCard";
import { getStripePromise } from "@/utils/stripe-helper";
import { userFacingErrorMessage } from "@/utils/userFacingError-helper";

/**
 * Collects a card for the ORGANIZATION, inline in the upgrade drawer.
 *
 * The organization's Stripe Customer is the subscription's billing identity, and a PaymentMethod
 * belongs to exactly one Customer — so a card saved against a GROUP (which is what the credits flow
 * collects, and what this drawer used to offer) cannot pay for a subscription however convenient it
 * looks. There is no way to share or clone one across Customers on a standard account, which is why
 * this collects its own rather than reusing the group's.
 *
 * `onSaved` receives the Stripe PaymentMethod id straight from the confirmed SetupIntent, so the
 * upgrade can proceed without waiting for the `setup_intent.succeeded` webhook. The webhook is not
 * involved here at all: nothing is mirrored into our tables for the organization's card.
 */

const CONSENT_TEXT =
  "I authorize Gifts for Good to securely save this card and charge it for the annual Good Pro " +
  "subscription, including automatic renewal.";

const linkButtonClass =
  "m-0 cursor-pointer border-0 bg-transparent p-0 font-semibold text-dark-dust underline decoration-dark-dust/40 hover:decoration-dark-dust";

function CardFields({
  onSaved,
  onCancel,
  ctaClassName,
  submitLabel,
  submittingLabel,
}: {
  onSaved: (paymentMethodId: string) => void;
  onCancel?: () => void;
  /** Submit-button footprint, supplied by the drawer that hosts this form. */
  ctaClassName?: string;
  submitLabel: string;
  submittingLabel: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    // redirect: "if_required" — a 3DS card opens Stripe's own modal rather than navigating away,
    // which matters more here than in the group modal: a page redirect would lose the drawer and
    // the upgrade the admin is halfway through.
    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message ?? "Couldn't save the card. Please try again.");
      setSubmitting(false);
      return;
    }

    const pm = setupIntent?.payment_method;
    const paymentMethodId = typeof pm === "string" ? pm : (pm?.id ?? null);
    if (!paymentMethodId) {
      // Confirmed with no PaymentMethod attached should not happen; failing loudly beats returning
      // to the drawer with a card that looks saved and an upgrade that cannot use it.
      setError("The card was saved but couldn't be read back. Please try again.");
      setSubmitting(false);
      return;
    }

    onSaved(paymentMethodId);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {/* wallets disabled for the same reason as the group modal: Stripe Link is a personal,
          cross-merchant wallet and would autofill the clicking admin's OWN card onto the
          organization's subscription. */}
      <PaymentElement
        options={{ wallets: { link: "never", applePay: "never", googlePay: "never" } }}
      />

      <label className="flex items-start gap-2 text-[12px] text-graphite">
        <input
          type="checkbox"
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 gfg-checkbox"
        />
        <span>{CONSENT_TEXT}</span>
      </label>

      {error && (
        <p role="alert" className="text-[#c00] text-[13px]">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={`${linkButtonClass} text-[13px]`}
          >
            Cancel
          </button>
        )}
        <GfgButton
          type="submit"
          disabled={!stripe || !consented || submitting}
          className={ctaClassName}
        >
          {submitting ? submittingLabel : submitLabel}
        </GfgButton>
      </div>
    </form>
  );
}

export default function SubscriptionCardForm({
  onSaved,
  onCancel,
  ctaClassName,
  // Defaults describe what this form does on its own — saves a card. Onboarding overrides them,
  // because there the same click also BUYS the subscription, and a button that says "Save card"
  // understates a $1,140 charge.
  submitLabel = "Save card",
  submittingLabel = "Saving…",
}: {
  onSaved: (paymentMethodId: string) => void;
  onCancel?: () => void;
  /** Submit-button footprint, supplied by the drawer that hosts this form. */
  ctaClassName?: string;
  submitLabel?: string;
  submittingLabel?: string;
}) {
  const { mutate: mintSetupIntent } = useSubscriptionSetupIntent();
  const { data: config } = usePublicConfig();
  const stripePromise = config?.stripePublishableKey
    ? getStripePromise(config.stripePublishableKey)
    : null;
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // One SetupIntent per mount. The drawer unmounts this when it closes, so reopening mints a fresh
  // one rather than reusing a secret that may already have been confirmed.
  useEffect(() => {
    if (config && !config.stripePublishableKey) {
      setError("Payments aren't configured (missing publishable key).");
      return;
    }
    if (!stripePromise) return; // config still loading
    mintSetupIntent(undefined, {
      onSuccess: (secret) => setClientSecret(secret),
      onError: (e) =>
        setError(
          userFacingErrorMessage(
            e,
            "Couldn't start adding a card. Please try again or contact support.",
          ),
        ),
    });
  }, [config, stripePromise, mintSetupIntent]);

  if (error) {
    return (
      <p role="alert" className="text-[#c00] text-[13px]">
        {error}
      </p>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div role="status" aria-label="Loading card form" className="h-24 rounded-card bg-wax" />
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardFields
        onSaved={onSaved}
        onCancel={onCancel}
        ctaClassName={ctaClassName}
        submitLabel={submitLabel}
        submittingLabel={submittingLabel}
      />
    </Elements>
  );
}
